<!-- ReviewSection.vue - Final review and add to cart -->
<template>
  <div class="review-section">

    <!-- Validation Errors from Store -->
    <div v-if="storeValidationErrors.length > 0" class="validation-errors">
      <div class="error-header">
        <svg class="error-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <span>Please resolve the following issues before adding to cart:</span>
      </div>
      <ul class="error-list">
        <li v-for="(error, index) in storeValidationErrors" :key="index">
          <button
            class="error-item-btn"
            :class="{ 'is-clickable': !!sectionForError(error) }"
            @click="handleErrorClick(error)"
          >
            {{ error }}
            <span v-if="sectionForError(error)" class="error-arrow">→</span>
          </button>
        </li>
      </ul>
    </div>

    <!-- DEBUG INFO -->
    <div v-if="debugConfig.showReviewDebugInfo" class="debug-info" style="background: #f0f0f0; padding: 10px; margin-bottom: 20px; font-size: 12px;">
      <strong>Debug Info:</strong><br>
      selectedArtworkMethod: {{ imprintStore.selectedArtworkMethod }}<br>
      selectedImprintMethod.sku: {{ imprintStore.selectedImprintMethod.sku }}<br>
      selectedImprintMethod.name: {{ imprintStore.selectedImprintMethod.name }}<br>
      hasSelections: {{ hasSelections }}<br>
      selectedArtworkMethodData: {{ selectedArtworkMethodData?.label }}<br>
      selectedImprintMethodData: {{ selectedImprintMethodData?.name }}
      <p>----selectionStore----</p>
      <p>colorSizeLines Full: {{selectionStore.colorSizeLines}}</p>
      <p>selected Colors Qty: {{selectionStore.getLinesByImprintKey(imprintStore.getImprintMethodKey).length}}</p>
      <p>selected Colors id: {{selectionStore.getSelectedColorsByImprintKey(imprintStore.getImprintMethodKey)}}</p>
      <p>selected sizes: {{selectionStore.getTotalSizesQtyByImprintKey(imprintStore.getImprintMethodKey)}}</p>
      <p>Special Instructions: {{specialInstructions}}</p>
    </div>
    
    <div class="review-summary">
      <!-- Selected Methods Summary -->
      <div v-if="debugConfig.showCustomizationMethodInfo" class="selected-methods-summary">
        <h4 class="subsection-title">Customization Method</h4>
        
        <!-- Artwork Method -->
        <div v-if="selectedArtworkMethodData" class="method-info">
          <div class="method-label">Artwork Method:</div>
          <div class="method-value">{{ selectedArtworkMethodData.label }}</div>
          <div v-if="selectedArtworkMethodData.description" class="method-description">
            {{ selectedArtworkMethodData.description }}
          </div>
        </div>
        
        <!-- Imprint Method -->
        <div v-if="selectedImprintMethodData" class="method-info">
          <div class="method-label">Imprint Method:</div>
          <div class="method-value">{{ selectedImprintMethodData.name }}</div>
          
          <!-- Method Image -->
          <div v-if="selectedImprintMethodData.image" class="method-image">
            <img 
              :src="selectedImprintMethodData.image" 
              :alt="selectedImprintMethodData.name"
              @error="handleImageError"
            />
          </div>
          
          <div v-if="selectedImprintMethodData.description" class="method-description">
            {{ selectedImprintMethodData.description }}
          </div>
        </div>
        
        <!-- No Selection State -->
        <div v-if="!hasSelections" class="no-selections">
          <p>No customization methods selected yet.</p>
          <p class="note">Please complete Step 2 to select your customization preferences.</p>
        </div>
      </div>
      
      <!-- Price Summary Tables -->
      <div class="price-summary">


        <!-- Single Combined Table -->
        <div class="summary-table-container">
          <table class="summary-table">
            <!-- Calendar Products Section (for calendar mode) -->
            <template v-if="appStore.isCalendarMode">
              <thead>
                <tr>
                  <th class="col-item">Selected Calendar Products</th>
                  <th class="col-qty">Quantity</th>
                  <th class="col-price">Unit Price</th>
                  <th class="col-subtotal">Sub Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="calendar in calendarProducts" :key="'calendar-' + calendar.id">
                  <td class="col-item calendar-product">
                    <div class="calendar-product-info">
                      <img v-if="calendar.image" :src="calendar.image" :alt="calendar.title" class="calendar-image" />
                      <span class="calendar-title">{{ calendar.title }}</span>
                    </div>
                  </td>
                  <td class="col-qty">{{ calendar.qty }}</td>
                  <td class="col-price">${{ formatPrice(calendar.unitPrice) }}</td>
                  <td class="col-subtotal">${{ formatPrice((calendar.unitPrice * calendar.qty) || 0) }}</td>
                </tr>
              </tbody>
            </template>
            
            <!-- Colors Section (for default mode) -->
            <template v-else>
              <thead>
                <tr>
                  <th class="col-item">{{ colorName }}</th>
                  <th class="col-qty">Quantity</th>
                  <th class="col-price">Unit Price</th>
                  <th class="col-subtotal">Sub Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in colorLines" :key="'color-' + line.id">
                  <td class="col-item">{{ line.slectedColor.title.replace(/-/g, ' ')}}</td>
                  <td class="col-qty">{{ line.totalQty }}</td>
                  <td class="col-price">${{ formatPrice(getColorPrice(line)) }}</td>
                  <td class="col-subtotal">${{ formatPrice(getColorLineTotal(line)) }}</td>
                </tr>
              </tbody>
            </template>

            <!-- Extra Charges Section -->
            <thead v-if="extraCharges.length > 0">
              <tr>
                <th class="col-item">Extra Charges</th>
                <th class="col-qty">Quantity</th>
                <th class="col-price">Unit Price</th>
                <th class="col-subtotal">Sub Total</th>
              </tr>
            </thead>
            <tbody v-if="extraCharges.length > 0">
              <tr v-for="charge in extraCharges" :key="'charge-' + charge.id">
                <td class="col-item">
                  <span class="charge-name-cell">
                    <span>{{ charge.name }}</span>
                    <span v-if="getFeeTooltip(charge.name)" class="fee-tooltip-wrap">
                      <span class="fee-tooltip-icon">?</span>
                      <span class="fee-tooltip-content">{{ getFeeTooltip(charge.name) }}</span>
                    </span>
                  </span>
                </td>
                <td class="col-qty">{{ charge.quantity }}</td>
                <td class="col-price">${{ formatPrice(charge.unitPrice) }}</td>
                <td class="col-subtotal">${{ formatPrice(charge.subTotal) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Total Row -->
        <div class="total-row">
          <span class="total-label">Total</span>
          <span class="total-amount">${{ formatPrice(calculateTotal()) }}</span>
        </div>
      </div>

      <!-- Special Instructions -->
      <div class="special-instructions">
        <label class="instructions-label">Special Instructions</label>
        <textarea
          v-model="specialInstructions"
          class="instructions-textarea"
          :class="{ 'has-error': validationErrors.specialInstructions }"
          placeholder="Add your instructions here"
          rows="3"
          maxlength="500"
          @input="onSpecialInstructionsChange"
        ></textarea>
        <div v-if="validationErrors.specialInstructions" class="error-message">
          {{ validationErrors.specialInstructions }}
        </div>
        <div class="char-counter">
<!--          {{ specialInstructions.length }} / 500 characters-->
        </div>
      </div>
      
      <!-- Imprint Details from Step 2 -->
      <div v-if="hasImprintData && debugConfig.showRawImprintData" class="imprint-details-section">
        <h4 class="subsection-title">Your Customization</h4>

        <!-- Display all imprints -->
        <ImprintSummary
          v-for="(imprint, index) in allImprints"
          :key="index"
          :title="getImprintTitle(index)"
          :imprint-data="imprint"
          :imprint-index="index"
        />
      </div>

      <!-- Full Order Summary Placeholder -->
      <div v-if="debugConfig.showMigrationInfo" class="order-summary-placeholder">
        <h4 class="subsection-title">Order Details</h4>
        <div class="migration-notes">
          <h4>Components to migrate:</h4>
          <ul>
            <li>imprintAppSummary.vue - Order summary</li>
            <li>Color & quantity selections</li>
            <li>Add-ons selection</li>
            <li>Final price calculation</li>
            <li>Validation before checkout</li>
          </ul>
        </div>
      </div>
    </div>
    
    <div class="action-buttons">
      <button class="btn-back" @click="$emit('go-back')" :disabled="isLoading">
        {{ previousStepButtonText }}
      </button>
      <div class="add-to-cart-wrap">
      <img
          v-if="isLoading"
          class="loader"
          src="https://imprint-uploads.s3.ca-central-1.amazonaws.com/assets/images/imprint_app/opc-ajax-loader.gif"
          alt="Adding to Cart..." title="Adding to Cart..."
      >

        <button
            class="btn-next btn-add-to-cart"
            :disabled="isLoading || appStore.isOutOfStock"
            :class="{ 'loading': isLoading, 'btn-disabled': appStore.isOutOfStock }"
            @click="handleAddToCart"
        >
          <span v-if="!isLoading">Add to Cart</span>
          <span v-else>Adding...</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useImprintStore } from '../stores/useImprintStore.js';
import { useSelectionStore } from '../stores/useSelectionStore.js';
import { useNewImprintStore } from '../stores/useNewImprintStore.js';
import { useAddToCartStore } from '../stores/useAddToCart.js';
import { useAppStore } from '../stores/useAppStore.js';
import { useCalendarStore } from '../stores/useCalendarStore.js';
import { ARTWORK_METHODS } from '../static_data/artworkMethods.js';
import { getImprintMethodBySku } from '../static_data/imprintMethodDescriptions.js';
import ImprintSummary from '../components/ImprintSummary.vue';
// import { computed } from 'vue';
import { DEBUG_CONFIG } from '../config/debug.js';
import { useSectionValidation } from '../composables/useSectionValidation.js';


// Import fee calculator utilities
import {
  calculateAllFees,
  getUniqueColorsCount,
  hasUploadedImage,
  getUniqueCustomColorsCount,
  combineProofFee,
  getFeeUnit
} from '../utils/feeCalculator.js';
import {getColorName} from "@/features/imprint/static_data/sections";

export default {
  name: 'ReviewSection',

  components: {
    ImprintSummary
  },


  data() {
    return {
      specialInstructions: '',
      validationErrors: {
        specialInstructions: ''
      }
    };
  },

  setup() {
    const imprintStore = useImprintStore();
    const newImprintStore = useNewImprintStore();
    const selectionStore = useSelectionStore();
    const addToCartStore = useAddToCartStore();
    const appStore = useAppStore();
    const calendarStore = useCalendarStore();
    const colorName = getColorName();

    // Use unified section validation (same as StepIndicator)
    const sectionValidation = useSectionValidation();

    console.log("SETUP");
    console.log(selectionStore);

    return {
      newImprintStore,
      imprintStore,
      selectionStore,
      addToCartStore,
      appStore,
      calendarStore,
      debugConfig: DEBUG_CONFIG,
      colorName,
      sectionValidation
    };
  },
  
  computed: {
    /**
     * Dynamic text for Previous Step button
     * Changes based on whether Add-Ons section is visible
     */
    previousStepButtonText() {
      if (this.debugConfig.showAddOnsSection) {
        return '← Previous Step: Add-Ons';
      }
      return '← Previous Step: Customize';
    },

    // Get selected artwork method data
    selectedArtworkMethodData() {
      const methodId = this.imprintStore.selectedArtworkMethod;
      
      console.log('[ReviewSection] Computing selectedArtworkMethodData, methodId:', methodId);
      
      if (!methodId) {
        console.log('[ReviewSection] No methodId');
        return null;
      }
      
      const found = ARTWORK_METHODS.find(m => m.id === methodId);
      console.log('[ReviewSection] Found artwork method:', found);
      
      return found;
    },
    
    // Get selected imprint method data
    selectedImprintMethodData() {
      const selected = this.imprintStore.selectedImprintMethod;
      
      console.log('[ReviewSection] Computing selectedImprintMethodData:', selected);
      
      if (!selected || !selected.sku) {
        console.log('[ReviewSection] No selected.sku');
        return null;
      }
      
      // Get enhanced data from static descriptions
      const enhancedData = getImprintMethodBySku(selected.sku);
      console.log('[ReviewSection] Enhanced data:', enhancedData);
      
      // Safely get image path
      let imagePath = null;
      if (enhancedData?.image) {
        imagePath = this.getImagePath(enhancedData.image);
      } else if (selected.data?.image) {
        imagePath = selected.data.image;
      }
      
      // Return data combining store data and enhanced descriptions
      return {
        name: enhancedData?.name || selected.name,
        sku: selected.sku,
        description: enhancedData?.description || selected.data?.description || '',
        image: imagePath, // Will be null if no image
        price: selected.data?.price || 0
      };
    },
    
    // Check if any selections have been made
    hasSelections() {
      return this.selectedArtworkMethodData || this.selectedImprintMethodData;
    },
    
    // Get color lines from store
    colorLines() {
      const imprintKey = this.imprintStore.getImprintMethodKey;
      if (!imprintKey) return [];

      return this.selectionStore.getLinesByImprintKey(imprintKey) || [];
    },

    // Get calendar products for calendar bundle mode
    calendarProducts() {
      if (!this.appStore.isCalendarMode) return [];
      
      console.log('[🎯 CALENDAR-PRICING] ReviewSection.calendarProducts called');
      
      const tierUnitPrice = this.calendarStore.unitPriceByTotalQty;
      const result = (this.calendarStore.products || []).map(product => ({
        ...product,
        unitPrice: tierUnitPrice
      }));
      
      console.log('[🎯 CALENDAR-PRICING] ReviewSection result:', result);
      
      return result;
    },

    // Get extra charges (fees, upcharges, add-ons)
    extraCharges() {
      const charges = [];

      // Get imprint data
      const sku = this.imprintStore.selectedImprintMethod.sku;
      if (!sku) return charges;

      const artworkMethod = this.imprintStore.selectedArtworkMethod || 'new';
      const imprintKey = this.imprintStore.getImprintMethodKey;
      
      // FIX: In calendar mode, get quantity from calendarStore, not selectionStore
      const totalQty = this.appStore.isCalendarMode 
        ? this.calendarStore.totalQuantity 
        : this.selectionStore.getTotalQtyByImprintKey(imprintKey);
      
      // Check if this is a Comda store
      const isComda = this.isComdaStore;
      
      // Get backend fee configuration
      const backendConfig = this.backendFeeConfig;
      
      // ✅ NEW LOGIC: Calculate fees for EACH imprint separately, then SUM them
      // CRITICAL FIX: For non-new artwork methods (previous artwork / send us artwork),
      // newImprintStore.imprints may still contain leftover data from a prior "Start New Imprint"
      // session. We must NOT use that stale data — treat it as a single empty imprint instead.
      const allImprints = artworkMethod === 'new'
        ? this.newImprintStore.imprints
        : [{ messageType: 'text', textLines: [], imageData: null }];
      
      // Initialize totals and per-imprint fees collector
      let totalSetupFee = 0;
      let totalSetupFeeQty = 0;
      let setupFeeUnitPrice = 0;
      let totalProofFee = 0;
      let totalRunningFee = 0;
      let totalImageFee = 0;
      let totalImageFeeQty = 0;
      let imageFeeUnitPrice = 0;
      let totalCustomColorFee = 0;
      let totalCustomColorFeeQty = 0;
      let customColorFeeUnitPrice = 0;
      const imprintsFees = []; // collects fee object from each imprint for combiners
      const additionalColorRunCharges = []; // individual line items per additional color

      console.log('[ReviewSection] Calculating fees for', allImprints.length, 'imprints');

      // Read fee_unit for proof fee from product fees array
      const feesArray = this.imprintStore.fullJson?.configurable_array?.[sku]?.fees || [];
      const proofFeeUnit = getFeeUnit(feesArray, 'PROOF', 'per order');
      const runningFeeRate = backendConfig.running_fee || 0;

      // Loop through each imprint and sum up fees
      allImprints.forEach((imprint, index) => {
        // Calculate fees for THIS imprint only
        const uniqueColorsCount = getUniqueColorsCount(imprint);
        
        // FIX: Count UNIQUE custom colors per imprint location
        // Each unique custom color should be charged separately
        const uniqueCustomColorsCount = getUniqueCustomColorsCount(imprint);
        
        // CRITICAL: hasImage should check BOTH:
        // 1. Image is uploaded (imageData exists)
        // 2. Message type allows images (image or text_image)
        const imageDataExists = hasUploadedImage(imprint);
        const messageType = imprint?.messageType || 'text';
        const messageTypeAllowsImage = messageType === 'image' || messageType === 'text_image';
        const hasImage = imageDataExists && messageTypeAllowsImage;

        // For running fee: count ALL colors (standard + custom)
        const uniqueColorsCountForRunning = getUniqueColorsCount(imprint, true);
        
        // First imprint location: first color is free (included in MLP base price)
        // Additional imprint locations: setup fee applies for ALL colors
        const isFirstImprintLocation = index === 0;
        
        // Calculate fees for this single imprint (NOT global!)
        const fees = calculateAllFees({
          uniqueColorsCount,
          uniqueColorsCountForRunning,
          totalQuantity: totalQty,
          hasPantoneColor: uniqueCustomColorsCount,
          hasImage: hasImage,
          artworkMethod,
          isComda,
          backendConfig,
          isFirstImprintLocation
        });
        
        console.log(`[ReviewSection] Imprint ${index} (${imprint.selectedLocation?.title || 'No location'}) fees:`, {
          uniqueColorsCount,
          uniqueCustomColorsCount,
          hasImage,
          fees
        });
        
        // Collect per-imprint fees for combiners
        imprintsFees.push(fees);

        // Setup fee — sum across all locations
        totalSetupFee += fees.setup_fee || 0;
        totalSetupFeeQty += fees.setup_fee_qty || 0;
        setupFeeUnitPrice = fees.setup_fee_unit_price || setupFeeUnitPrice;

        // Sum fees that are always per-location
        totalImageFee += fees.image_fee || 0;
        totalImageFeeQty += fees.image_fee_qty || 0;
        imageFeeUnitPrice = fees.image_fee_unit_price || imageFeeUnitPrice;
        totalCustomColorFee += fees.customColourFee || 0;
        totalCustomColorFeeQty += fees.customColourFee_qty || 0;
        customColorFeeUnitPrice = fees.customColourFee_unit_price || customColorFeeUnitPrice;

        // Build per-color running fee line items
        // Collect all additional colors (standard + custom) from this imprint
        if (runningFeeRate > 0 && artworkMethod === 'new') {
          const allColorsInImprint = [];
          const messageTypeLocal = imprint?.messageType || 'text';

          // Collect text line colors
          if (messageTypeLocal === 'text' || messageTypeLocal === 'text_image') {
            (imprint.textLines || []).forEach(line => {
              if (!line || line.type === 'image') return;
              const hasText = typeof line.message === 'string' && line.message.trim().length > 0;
              if (!hasText) return;
              if (line.selectedColor) {
                allColorsInImprint.push({
                  color: line.selectedColor,
                  isCustom: line.customColor === true
                });
              }
            });
          }

          // Collect image line color (for 'image' and 'text_image' modes)
          if (messageTypeLocal === 'image' || messageTypeLocal === 'text_image') {
            (imprint.textLines || []).forEach(line => {
              if (!line || line.type !== 'image') return;
              const color = line.selectedColor || line.selectedColorObj?.file_name;
              if (color) {
                allColorsInImprint.push({ color, isCustom: false });
              }
            });
          }

          // Deduplicate by color key
          const seen = new Set();
          const uniqueAll = allColorsInImprint.filter(c => {
            const key = (c.isCustom ? 'custom:' : 'std:') + c.color;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });

          // Skip first color (included in base price), charge for the rest
          if (uniqueAll.length > 1) {
            uniqueAll.slice(1).forEach(c => {
              const colorLabel = c.color.replace(/-/g, ' ').trim();
              const chargeId = `running_fee_${index}_${c.color}`;
              additionalColorRunCharges.push({
                id: chargeId,
                name: `Additional Colour Run Fee (${colorLabel})`,
                quantity: totalQty,
                unitPrice: runningFeeRate,
                subTotal: runningFeeRate * totalQty
              });
              totalRunningFee += runningFeeRate * totalQty;
            });
          }
        }
      });

      // Use combiner only for proof fee (per-order logic)
      totalProofFee = combineProofFee(imprintsFees, proofFeeUnit);
      
      console.log('[ReviewSection] Total fees (sum of all imprints):', {
        totalSetupFee,
        totalProofFee,
        totalRunningFee,
        totalImageFee,
        totalCustomColorFee
      });

      // Add summed fees to charges (show only if > 0 or not COMDA)
      
      // Setup Fee (sum of all imprints)
      if (totalSetupFee > 0 || !isComda) {
        charges.push({
          id: 'setup_fee',
          name: `Setup Fee`,
          quantity: totalSetupFeeQty,
          unitPrice: setupFeeUnitPrice,
          subTotal: totalSetupFee
        });
      }

      // Proof Fee (sum of all imprints)
      if (totalProofFee > 0 || !isComda) {
        charges.push({
          id: 'proof_fee',
          name: `Proof Fee`,
          quantity: totalProofFee > 0 ? 1 : 0,
          unitPrice: totalProofFee,
          subTotal: totalProofFee
        });
      }
      
      // Additional Colour Run Fee — one line per additional color per location
      additionalColorRunCharges.forEach(charge => {
        charges.push(charge);
      });
      
      // Image Fee (sum of all imprints)
      if (totalImageFee > 0 || !isComda) {
        charges.push({
          id: 'image_fee',
          name: `Image Upload Fee`,
          quantity: totalImageFeeQty,
          unitPrice: imageFeeUnitPrice,
          subTotal: totalImageFee
        });
      }
      
      // Custom Colour Fee (sum of all imprints)
      if (totalCustomColorFee > 0 || !isComda) {
        charges.push({
          id: 'custom_colour_fee',
          name: `Custom Color Fee`,
          quantity: totalCustomColorFeeQty,
          unitPrice: customColorFeeUnitPrice,
          subTotal: totalCustomColorFee
        });
      }

      // Size Upcharges
      if (this.imprintStore.sizeUpcharge && Object.keys(this.imprintStore.sizeUpcharge).length > 0) {
        const imprintKey = this.imprintStore.getImprintMethodKey;
        const totalSizesQty = this.selectionStore.getTotalSizesQtyByImprintKey(imprintKey);

        for (const [size, qty] of Object.entries(totalSizesQty)) {
          const upcharge = this.imprintStore.sizeUpcharge[size];
          if (upcharge && Number(upcharge) > 0 && Number(qty) > 0) {
            charges.push({
              id: `size_upcharge_${size}`,
              name: `Apparel Upsize (${size})`,
              quantity: Number(qty),
              unitPrice: Number(upcharge),
              subTotal: Number(upcharge) * Number(qty)
            });
          }
        }
      }

      // Color Upcharges/Downcharges
      if (this.imprintStore.colorUpcharge && Object.keys(this.imprintStore.colorUpcharge).length > 0) {
        const imprintKey = this.imprintStore.getImprintMethodKey;
        const colorLines = this.selectionStore.getLinesByImprintKey(imprintKey) || [];

        for (const line of colorLines) {
          const colorName = line.slectedColor?.title?.toLowerCase() || '';
          const colorUpchargeData = this.imprintStore.colorUpcharge[colorName];
          
          if (colorUpchargeData && line.totalQty > 0) {
            const qty = Number(line.totalQty);
            let upchargeAmount = 0;
            
            // Check price_type: 'fixed', empty string, or undefined/null = fixed; 'percent' = percent
            const priceType = colorUpchargeData.price_type;
            const isFixed = !priceType || priceType === 'fixed' || priceType === '';
            
            if (isFixed) {
              upchargeAmount = Number(colorUpchargeData.price || 0);
            } else if (priceType === 'percent') {
              // For percent, calculate based on base price per item
              const basePrice = this.getColorPrice(line);
              const percent = Number(colorUpchargeData.price || 0);
              upchargeAmount = basePrice * (percent / 100);
            }
            
            // Show charge if amount is not zero (can be positive or negative)
            if (upchargeAmount !== 0) {
              const colorTitle = line.slectedColor?.title?.replace(/-/g, ' ') || colorName;
              // Use "Downcharge" for negative values, "Upcharge" for positive
              const chargeType = upchargeAmount < 0 ? 'Downcharge' : 'Upcharge';
              charges.push({
                id: `color_upcharge_${colorName}`,
                name: `Color ${chargeType} (${colorTitle})`,
                quantity: qty,
                unitPrice: upchargeAmount,
                subTotal: upchargeAmount * qty
              });
            }
          }
        }
      }

      // Additional Location Fee — applies from the 2nd imprint onward
      // Fee is per-piece × total quantity, charged once per additional location
      if (allImprints.length > 1) {
        const additionalLocationsCount = allImprints.length - 1;
        const feePerPiece = this.imprintStore.getAdditionalLocationFeePerPiece(totalQty);

        if (feePerPiece > 0) {
          for (let i = 0; i < additionalLocationsCount; i++) {
              // FIX: allImprints here is raw newImprintStore.imprints — field is selectedLocation, not location
              const locationTitle = allImprints[i + 1]?.selectedLocation?.title || `Location ${i + 2}`;
            charges.push({
              id: `additional_location_fee_${i + 1}`,
              name: `Additional Location Fee (${locationTitle})`,
              quantity: totalQty,
              unitPrice: feePerPiece,
              subTotal: feePerPiece * totalQty
            });
          }
        }

        console.log('[ReviewSection] Additional location fees:', {
          additionalLocationsCount,
          feePerPiece,
          totalQty
        });
      }

      // TODO: Add-ons will be added here when Add-Ons section is implemented

      return charges;
    },

    // Validation - check if can proceed to cart
    // Get all imprints data
    allImprints() {
      return this.newImprintStore.imprints.map((imprint, index) => {
        return this.newImprintStore.imprints[index] ?
          {
            ...this.newImprintStore.getImprintData(),
            location: imprint.selectedLocation,
            messageType: imprint.messageType,
            textLines: imprint.textLines,
            imageData: imprint.imageData,
            specialInstructions: imprint.specialInstructions
          } : null;
      }).filter(Boolean);
    },

    // Check if has any imprint data
    hasImprintData() {
      return this.newImprintStore.imprints.length > 0 &&
             this.newImprintStore.imprints.some(imp =>
               imp.selectedLocation?.sku ||
               imp.textLines?.length > 0 ||
               imp.imageData
             );
    },

    // Validation - uses unified sectionValidation
    isValid() {
      console.log('[ReviewSection] Computing isValid');
      
      // Check store readiness
      if (!this.addToCartStore.isReadyToAddToCart) {
        console.log('[ReviewSection] Store not ready');
        return false;
      }

      // Check if not currently loading
      if (this.addToCartStore.loader) {
        console.log('[ReviewSection] Currently loading');
        return false;
      }

      // Check local validation errors (special instructions)
      if (Object.values(this.validationErrors).some(error => error !== '')) {
        console.log('[ReviewSection] Has local validation errors');
        return false;
      }

      // Check unified sectionValidation - this is the main validation
      if (this.storeValidationErrors.length > 0) {
        console.log('[ReviewSection] Has sectionValidation errors:', this.storeValidationErrors.length);
        return false;
      }
      
      console.log('[ReviewSection] isValid: true');
      return true;
    },

    // Check if currently loading
    isLoading() {
      return this.addToCartStore.loader;
    },

    // Get validation errors - using unified sectionValidation (same as StepIndicator)
    // Each error prefixed with Step number
    // Note: For calendar mode, Step 1 (Colour & Quantity) is skipped, so numbering starts from Step 1 = Customize
    storeValidationErrors() {
      const allErrors = [];
      const isCalendarMode = this.appStore.applicationType === 'calendar';

      // Step 1: Colour & Quantity (only for default mode)
      if (!isCalendarMode) {
        const step1Errors = this.sectionValidation.step1Validation.value.errors;
        step1Errors.forEach(error => {
          allErrors.push(`Step 1: ${error}`);
        });
      }

      // Step 2: Customize Your Product (Step 1 for calendar mode)
      const step2Errors = this.sectionValidation.step2Validation.value.errors;
      const customizeStepNumber = isCalendarMode ? 1 : 2;
      step2Errors.forEach(error => {
        allErrors.push(`Step ${customizeStepNumber}: ${error}`);
      });

      // Step 3: Add-Ons (Step 2 for calendar mode) - optional, usually empty
      const step3Errors = this.sectionValidation.step3Validation.value.errors;
      const addOnsStepNumber = isCalendarMode ? 2 : 3;
      step3Errors.forEach(error => {
        allErrors.push(`Step ${addOnsStepNumber}: ${error}`);
      });

      return allErrors;
    },
    
    // Check if this is a Comda store
    isComdaStore() {
      // Check store domain or ID
      const domain = window.Shopify?.shop || '';
      return domain.includes('comda');
    },
    
    // Get backend fee configuration
    backendFeeConfig() {
      const sku = this.imprintStore.selectedImprintMethod.sku;
      if (!sku || !this.imprintStore.fullJson) {
        console.warn('[ReviewSection] No SKU or fullJson for backend config');
        return {};
      }
      
      const methodData = this.imprintStore.fullJson.configurable_array?.[sku];
      if (!methodData?.configurable_2_data?.imprint_data) {
        console.warn('[ReviewSection] No imprint_data for SKU:', sku);
        return {};
      }
      
      const imprintData = methodData.configurable_2_data.imprint_data;
      
      const config = {
        setup_fee: imprintData.setup_fee || 0,
        running_fee: imprintData.running_fee || 0,
        custom_colour_fee: imprintData.custom_colour_fee || 0,
        image_fee: imprintData.image_fee || 0,
        proof_fee: imprintData.proof_fee || 0
      };
      
      console.log('[ReviewSection] Backend fee config:', config);
      return config;
    }
  },
  
  methods: {
    /**
     * Map error text to section ID for scroll navigation.
     * Returns null if no scroll target available.
     * @param {string} error
     * @returns {string|null}
     */
    sectionForError(error) {
      const isCalendar = this.appStore.isCalendarMode;
      if (error.startsWith('Step 1:') && !isCalendar) return 'colourQuantity';
      if (error.startsWith('Step 1:') && isCalendar) return 'imprintSelection';
      if (error.startsWith('Step 2:')) return 'imprintSelection';
      return null;
    },

    /**
     * Parse error text → CSS selector for the specific DOM field.
     * Returns null when no specific field can be targeted (e.g. Step 1 errors).
     *
     * Supported error patterns (after stripping "Step N: " prefix):
     *   "Imprint 1, Line 1: Please enter text"       → text input
     *   "Imprint 1, Line 1: Please select a colour"  → colour swatch
     *   "Imprint 1: Please upload an image"           → image upload zone
     *   "Imprint 1: Please select image colour"       → image colour swatch
     *   "Imprint 1: Please select a location"         → location selector (section only)
     * @param {string} error
     * @returns {string|null}
     */
    fieldSelectorForError(error) {
      if (!error.startsWith('Step 2:')) return null;

      // Strip "Step 2: " prefix
      const text = error.replace(/^Step \d+:\s*/, '');

      // "Imprint N, Line N: ..."
      const lineMatch = text.match(/^Imprint (\d+), Line (\d+):/);
      if (lineMatch) {
        const imprintIdx = parseInt(lineMatch[1]) - 1;
        const lineIdx   = parseInt(lineMatch[2]) - 1;
        if (text.includes('enter text')) {
          return `[data-error-field="imprint-${imprintIdx}-line-${lineIdx}-text"]`;
        }
        // "select a colour" / "select a color"
        return `[data-error-field="imprint-${imprintIdx}-line-${lineIdx}-color"]`;
      }

      // "Imprint N: ..."
      const imprintMatch = text.match(/^Imprint (\d+):/);
      if (imprintMatch) {
        const imprintIdx = parseInt(imprintMatch[1]) - 1;
        if (text.toLowerCase().includes('upload an image')) {
          return `[data-error-field="imprint-${imprintIdx}-image-upload"]`;
        }
        if (text.toLowerCase().includes('image')) {
          return `[data-error-field="imprint-${imprintIdx}-image-color"]`;
        }
        // location — no specific field element, fall back to section scroll
      }

      return null;
    },

    /**
     * Handle click on error item — emit scroll-to-field (or scroll-to-section as fallback).
     * Also triggers field-level validation so red borders appear on form fields.
     * @param {string} error
     */
    handleErrorClick(error) {
      // Trigger field-level validation to show has-error CSS class on fields
      if (this.imprintStore.selectedArtworkMethod === 'new') {
        this.newImprintStore.imprints.forEach((_, index) => {
          this.newImprintStore.validate(index);
        });
      }

      const fieldSelector = this.fieldSelectorForError(error);
      const sectionId     = this.sectionForError(error);

      if (fieldSelector && sectionId) {
        // Scroll to the specific field element inside the section
        this.$emit('scroll-to-field', { sectionId, fieldSelector });
      } else if (sectionId) {
        // Fall back to section-level scroll (Step 1 errors, location error, etc.)
        this.$emit('scroll-to-section', sectionId);
      }
    },

    getImprintTitle(index) {
      const titles = ['First Imprint', 'Second Imprint', 'Third Imprint', 'Fourth Imprint', 'Fifth Imprint'];
      return titles[index] || `Imprint ${index + 1}`;
    },

    /**
     * Returns tooltip text for a given extra charge name.
     * Handles exact matches (case-insensitive) and prefix matches (e.g. "Apparel Upsize (3XL)").
     * @param {string} name - charge name from extraCharges array
     * @returns {string|null}
     */
    getFeeTooltip(name) {
      const FEE_TOOLTIPS = {
        'setup fee': 'The industry standard cost associated with setting up the screen, die, laser, or other necessary items to imprint your specific logo.',
        'proof fee': 'The cost to digitize your artwork (company name/logo/message) and digitally render it on a product for review prior to production.',
        'image fee': 'The fees associated with formatting, retouching, and/or redrawing custom logos.',
        'image upload fee': 'The fees associated with formatting, retouching, and/or redrawing custom logos.',
        'custom color fee': 'PMS (Pantone) color match fee, applied when creating non-standard imprint colors (matching to existing logo, etc.).',
        'colour fee': 'PMS (Pantone) color match fee, applied when creating non-standard imprint colors (matching to existing logo, etc.).',
        'color fee': 'PMS (Pantone) color match fee, applied when creating non-standard imprint colors (matching to existing logo, etc.).',
        'running fee': 'Pricing varies by product color, size, and material. Standard pricing includes 1 color imprint on standard products. 2XL sizes, different fabrics, etc. may be customized for additional fees.',
        'shipping fee': 'The cost to cover the shipping and handling fees from our freight provider. To simplify the order process, all MLP orders qualify for free shipping!',
        'apparel upsize': 'Pricing based on extra sizes.',
      };

      const key = (name || '').toLowerCase().trim();

      // Exact match
      if (FEE_TOOLTIPS[key]) return FEE_TOOLTIPS[key];

      // Prefix match for names like "Apparel Upsize (3XL)"
      for (const prefix of Object.keys(FEE_TOOLTIPS)) {
        if (key.startsWith(prefix)) return FEE_TOOLTIPS[prefix];
      }

      if (key.includes('run fee')) return FEE_TOOLTIPS['running fee'];

      return null;
    },

    handleImageError(event) {
      console.warn('[ReviewSection] Failed to load image:', event.target.src);
      // Hide image container on error
      event.target.style.display = 'none';
    },
    
    getImagePath(filename) {
      // Import image dynamically from assets
      try {
        return new URL(`../../../assets/imprint-methods/${filename}`, import.meta.url).href;
      } catch (e) {
        console.warn('[ReviewSection] Failed to load imprint method image:', filename, e);
        return null;
      }
    },

    formatPrice(price) {
      return Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    
    /**
     * Check if sale price is valid based on current date
     * @param {Object} tier - Tier price object
     * @returns {boolean} - True if sale price is valid
     */
    isSalePriceValid(tier) {
      // If no sale_price, return false
      if (!tier.sale_price) {
        return false;
      }
      
      // If no dates provided, sale price is NOT valid (safety first)
      if (!tier.sale_price_effectivedate || !tier.sale_price_terminationdate) {
        console.warn('[ReviewSection] Sale price missing dates, using regular price', tier);
        return false;
      }
      
      // Get current date (midnight for consistent comparison)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Parse dates from backend (format: "YYYY-MM-DD")
      const effectiveDate = new Date(tier.sale_price_effectivedate);
      effectiveDate.setHours(0, 0, 0, 0);
      
      const terminationDate = new Date(tier.sale_price_terminationdate);
      terminationDate.setHours(23, 59, 59, 999); // End of day
      
      // Check if today is within the valid range
      const isValid = today >= effectiveDate && today <= terminationDate;
      
      console.log('[ReviewSection] Sale price date check:', {
        today: today.toISOString().split('T')[0],
        effectiveDate: tier.sale_price_effectivedate,
        terminationDate: tier.sale_price_terminationdate,
        isValid
      });
      
      return isValid;
    },

    // Get base price for color line
    getColorPrice(line) {
      const colorName = line.slectedColor?.title?.toLowerCase() || '';
      const colorUpcharge = this.imprintStore.colorUpcharge?.[colorName];

      // Get base price from tier prices based on quantity
      const sku = this.imprintStore.selectedImprintMethod.sku;
      if (!sku) {
        console.warn('[ReviewSection] getColorPrice: No SKU');
        return 0;
      }

      const methodData = this.imprintStore.fullJson?.configurable_array?.[sku];
      if (!methodData) {
        console.warn('[ReviewSection] getColorPrice: No method data for SKU:', sku);
        return 0;
      }

      // const quantity = Number(line.totalQty || 0);

      const imprintKey = this.imprintStore.getImprintMethodKey;
      const quantity = this.selectionStore.getTotalQtyByImprintKey(imprintKey);

      let basePrice = 0;

      // Get price from tier_price array based on quantity
      const tierPrices = methodData.prices_2?.tier_price || [];

      console.log('[ReviewSection] getColorPrice:', {
        colorName,
        quantity,
        hasTierPrices: tierPrices.length > 0,
        tierPricesCount: tierPrices.length,
        min_price: methodData.prices_2?.min_price
      });

      if (tierPrices.length > 0) {
      // Find appropriate tier price
      // Sort by QtyMin descending to find the highest tier that applies
      const sortedTiers = [...tierPrices].sort((a, b) => Number(b.QtyMin || 0) - Number(a.QtyMin || 0));

      const b2bTiers = this.appStore.getB2BTierForSku(sku);

      for (const tier of sortedTiers) {
      if (quantity >= Number(tier.QtyMin || 0)) {
          const b2bEntry = b2bTiers
              ? b2bTiers.find(t => Number(t.QtyMin) === Number(tier.QtyMin))
              : null;
          if (b2bEntry) {
              basePrice = Number(b2bEntry.price);
              console.log('[ReviewSection] Using B2B price:', basePrice, 'for qty:', quantity);
          } else if (this.isSalePriceValid(tier)) {
              basePrice = Number(tier.sale_price);
              console.log('[ReviewSection] Using VALID sale_price:', basePrice, 'for qty:', quantity);
          } else {
              basePrice = Number(tier.price || 0);
              console.log('[ReviewSection] Using regular price:', basePrice, 'for qty:', quantity, '(sale not valid)');
          }
        break;
        }
      }

      // If no tier found, use the lowest tier (first in original array)
      if (basePrice === 0 && tierPrices.length > 0) {
      const lowestTier = tierPrices[0];
          const b2bEntry = b2bTiers
              ? b2bTiers.find(t => Number(t.QtyMin) === Number(lowestTier.QtyMin))
              : null;
          if (b2bEntry) {
              basePrice = Number(b2bEntry.price);
          } else if (this.isSalePriceValid(lowestTier)) {
              basePrice = Number(lowestTier.sale_price);
              console.log('[ReviewSection] Using lowest tier VALID sale_price:', basePrice);
          } else {
              basePrice = Number(lowestTier.price || 0);
              console.log('[ReviewSection] Using lowest tier regular price:', basePrice, '(sale not valid)');
          }
      }
      } else {
      // Fallback to min_price if no tier prices
        // Check if sale_min_price exists and is valid
        const pricesData = methodData.prices_2;
        if (pricesData.sale_min_price && 
            pricesData.sale_min_price_effectivedate && 
            pricesData.sale_min_price_terminationdate) {
          // Check dates for sale_min_price
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const effectiveDate = new Date(pricesData.sale_min_price_effectivedate);
          effectiveDate.setHours(0, 0, 0, 0);
          
          const terminationDate = new Date(pricesData.sale_min_price_terminationdate);
          terminationDate.setHours(23, 59, 59, 999);
          
          if (today >= effectiveDate && today <= terminationDate) {
            basePrice = Number(pricesData.sale_min_price);
            console.log('[ReviewSection] Using VALID sale_min_price as fallback:', basePrice);
          } else {
            basePrice = Number(pricesData.min_price || 0);
            console.log('[ReviewSection] Using min_price as fallback:', basePrice, '(sale_min_price not valid)');
          }
        } else {
          basePrice = Number(pricesData.min_price || 0);
          console.log('[ReviewSection] Using min_price as fallback:', basePrice, '(no sale_min_price)');
        }
      }

      // NOTE: Color upcharge is now calculated separately in extraCharges
      // We no longer apply it to unit price here to avoid double-counting
      // The upcharge appears as a separate line item in the Extra Charges table
      if (colorUpcharge) {
        console.log('[ReviewSection] Color upcharge found for', colorName, ':', colorUpcharge);
        console.log('[ReviewSection] Color upcharge will be shown as separate line in Extra Charges');
      }

      console.log('[ReviewSection] Final price:', basePrice);
      return basePrice;
    },

    // Calculate total for color line
    getColorLineTotal(line) {
      const unitPrice = this.getColorPrice(line);
      const quantity = Number(line.totalQty || 0);
      return unitPrice * quantity;
    },

    // Calculate grand total
    calculateTotal() {
      let total = 0;
      
      // For calendar mode - sum calendar products
      if (this.appStore.isCalendarMode) {
        this.calendarProducts.forEach(calendar => {
          total += (calendar.qty || 0) * (calendar.unitPrice || 0);
        });
      } else {
        // For default mode - sum colors
        this.colorLines.forEach(line => {
          total += this.getColorLineTotal(line);
        });
      }

      // Add extra charges
      this.extraCharges.forEach(charge => {
        total += Number(charge.subTotal || 0);
      });

      return total;
    },

    // Handle special instructions change
    onSpecialInstructionsChange() {
      // Validate length
      if (this.specialInstructions.length > 500) {
        this.validationErrors.specialInstructions = 'Instructions cannot exceed 500 characters';
      } else {
        this.validationErrors.specialInstructions = '';
      }

      // Save to store
      this.selectionStore.setSpecialInstructions(this.specialInstructions);
    },

    // Validate all fields
    validateAll() {
      let isValid = true;

      // Validate special instructions
      if (this.specialInstructions.length > 500) {
        this.validationErrors.specialInstructions = 'Instructions cannot exceed 500 characters';
        isValid = false;
      }

      // SKIP color selections check for calendar mode
      if (!this.appStore.isCalendarMode) {
        // Check if has color selections (default mode only)
        if (this.colorLines.length === 0) {
          console.warn('[ReviewSection] No color selections');
          isValid = false;
        }
      }

      return isValid;
    },

    async handleAddToCart() {
      // Hard stop: never add an out-of-stock product to cart
      if (this.appStore.isOutOfStock) {
        console.warn('[ReviewSection] Add to Cart blocked: product is Out of Stock');
        return;
      }

      // Always validate first (button is no longer disabled by isValid)
      if (this.storeValidationErrors.length > 0) {
        // Trigger field-level validation to show red borders on individual fields
        if (this.imprintStore.selectedArtworkMethod === 'new') {
          this.newImprintStore.imprints.forEach((_, index) => {
            this.newImprintStore.validate(index);
          });
        }

        // Collect section IDs that have errors for shake + scroll
        const sectionsWithErrors = [];
        if (!this.appStore.isCalendarMode && this.sectionValidation.step1Validation.value.hasErrors) {
          sectionsWithErrors.push('colourQuantity');
        }
        if (this.sectionValidation.step2Validation.value.hasErrors) {
          sectionsWithErrors.push('imprintSelection');
        }
        this.$emit('validation-failed', sectionsWithErrors);
        return;
      }

      if (!this.validateAll()) {
        console.warn('[ReviewSection] Validation failed');
        return;
      }

      if (!this.isValid) {
        console.warn('[ReviewSection] Cannot add to cart - validation failed');
        return;
      }

      // Save special instructions to store before adding to cart
      this.selectionStore.setSpecialInstructions(this.specialInstructions);

      console.log('[ReviewSection] Adding to cart with selections:', {
        artworkMethod: this.imprintStore.selectedArtworkMethod,
        imprintMethod: this.imprintStore.selectedImprintMethod,
        specialInstructions: this.specialInstructions,
        total: this.calculateTotal()
      });

      // Emit to parent component which will handle the actual add to cart
      this.$emit('add-to-cart');
    }
  },
  
  mounted() {
    // Load Open Sans explicitly to ensure consistent bold rendering across all store themes
    // (some themes like Comda do not load Open Sans globally)
    if (!document.querySelector('link[href*="Open+Sans"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap';
      document.head.appendChild(link);
    }

    console.log('[ReviewSection] Mounted - Store data:', {
      artworkMethod: this.imprintStore.selectedArtworkMethod,
      imprintMethod: this.imprintStore.selectedImprintMethod,
      availableMethods: this.imprintStore.availableImprintMethods
    });

    // Load special instructions from store if available
    this.specialInstructions = this.selectionStore.specialInstructions || '';
  }
}
</script>

<style scoped lang="scss">
@use '@/styles/components/_review-section';
</style>
